/**
 * VehicleMultiSelectField
 * A checkbox list for selecting which of the account's vehicles to display.
 * Used by the Fleet Carousel section to let operators hand-pick vehicles.
 */

import { useEffect, useRef } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import type { Vehicle } from '@limo/database'

interface VehicleMultiSelectFieldProps {
  value: string[] | undefined
  onChange: (ids: string[]) => void
  accountId: string
  label: string
  description?: string
}

export function VehicleMultiSelectField({
  value,
  onChange,
  accountId,
  label,
  description,
}: VehicleMultiSelectFieldProps) {
  const {
    data: vehicles,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['vehicles', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('account_id', accountId)
        .order('display_order')
      if (error) throw error
      return data as Vehicle[]
    },
    enabled: !!accountId,
  })

  const selectedIds = value || []

  // Default to all vehicles selected when the picker is first shown with an
  // empty selection (i.e. the operator just enabled "Select Specific Vehicles").
  // Runs once per mount, so deselecting vehicles afterwards is respected.
  const initializedRef = useRef(false)
  useEffect(() => {
    if (initializedRef.current) return
    if (!vehicles) return // still loading — try again once loaded
    initializedRef.current = true
    if (vehicles.length > 0 && selectedIds.length === 0) {
      onChange(vehicles.map((v) => v.id))
    }
  }, [vehicles, selectedIds, onChange])

  const toggleVehicle = (vehicleId: string, checked: boolean) => {
    // Build a new array (immutable) — never mutate the existing selection
    const newValue = checked
      ? [...selectedIds, vehicleId]
      : selectedIds.filter((id) => id !== vehicleId)
    onChange(newValue)
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading vehicles...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Failed to load vehicles
        </div>
      </div>
    )
  }

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">
          No vehicles yet — add vehicles first to choose which ones display.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="border rounded-lg p-3 max-h-[220px] overflow-y-auto space-y-2">
        {vehicles.map((vehicle) => (
          <div key={vehicle.id} className="flex items-center space-x-2">
            <Checkbox
              id={`vehicle-${vehicle.id}`}
              checked={selectedIds.includes(vehicle.id)}
              onCheckedChange={(checked) =>
                toggleVehicle(vehicle.id, checked === true)
              }
            />
            <label
              htmlFor={`vehicle-${vehicle.id}`}
              className="text-sm font-medium leading-none cursor-pointer"
            >
              {vehicle.name}
            </label>
          </div>
        ))}
      </div>
      {selectedIds.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          No vehicles selected — the carousel will be hidden until you check at
          least one.
        </p>
      )}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}
